import { useCallback } from 'react';

import { buildCycleAudiencePayloadIfHydrated } from './audiencePayload';
import {
    buildCycleInformationAutosavePayload,
    hasCycleInformationDraftChanged,
} from './cycleInformationAutosave';
import { buildCycleEvaluationPeriodPayload } from './periodPayload';

import type { CycleInformationFormData } from './validation';
import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { TranslationKey } from '@/i18n-key';
import type { EvaluationPeriodOption } from '@/pages/ManagerAssessment/Evaluation/types';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { UseFormGetValues, UseFormTrigger } from 'react-hook-form';

import {
    isDuplicateCycleNameError,
    useCreateCycleEvaluationGroup,
    useUpdateCycleEvaluationGroup,
} from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import { CycleEvaluationGroupSteps } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { isCycleEvaluationGroupLaunched } from '@/navigation/CycleEvaluation/cycleEvaluationGroupLaunched';
import {
    buildCycleEvaluationGroupUpdatePayload,
    getCycleEvaluationGroupStepPath,
} from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { RouteName } from '@/navigation/types';
import { showWarningToast } from '@/plugins/Toast';

const DUPLICATE_NAME_MESSAGE: TranslationKey =
    'cycleEvaluationCycleInformation.validation.nameAlreadyUsed';

interface UseCycleInformationSubmitParams {
    isReadOnly: boolean;
    isNewCycle: boolean;
    group?: CycleEvaluationGroup;
    cycleEvaluationGroupId?: string;
    trigger: UseFormTrigger<CycleInformationFormData>;
    getValues: UseFormGetValues<CycleInformationFormData>;
    periods: EvaluationPeriodOption[];
    offeredPeriodValues: readonly string[];
    filters: SelectedFilters;
    isAudienceHydrated: boolean;
}

export const useCycleInformationSubmit = ({
    isReadOnly,
    isNewCycle,
    group,
    cycleEvaluationGroupId,
    trigger,
    getValues,
    periods,
    offeredPeriodValues,
    filters,
    isAudienceHydrated,
}: UseCycleInformationSubmitParams) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const navigate = useNavigate();

    const { mutateAsync: createCycleEvaluationGroup } = useCreateCycleEvaluationGroup();
    const { mutateAsync: updateCycleEvaluationGroup } = useUpdateCycleEvaluationGroup();

    return useCallback(async () => {
        if (isReadOnly) {
            return false;
        }

        const isLaunched = isCycleEvaluationGroupLaunched(group);

        // A launched cycle whose fields all still read what the server holds has nothing to
        // validate or write: "Next" merely moves on, the same as any other untouched step.
        if (group && isLaunched) {
            const draftValues = getValues();
            const payload = buildCycleInformationAutosavePayload({
                draft: {
                    name: draftValues.name,
                    selectedPeriod: draftValues.selectedPeriod,
                    periodStart: draftValues.periodStart,
                    periodEnd: draftValues.periodEnd,
                    audienceType: draftValues.audienceType,
                    filters,
                },
                group,
                periods,
                offeredPeriodValues,
                isAudienceHydrated,
            });

            if (!hasCycleInformationDraftChanged({ group, payload })) {
                return true;
            }
        }

        const isValid = await trigger();

        if (!isValid) {
            return false;
        }

        if (!isNewCycle && (!group || !cycleEvaluationGroupId)) {
            return false;
        }

        const values = getValues();

        const period = buildCycleEvaluationPeriodPayload({
            selectedPeriod: values.selectedPeriod,
            periodStart: values.periodStart,
            periodEnd: values.periodEnd,
            periods,
        });

        const audience = buildCycleAudiencePayloadIfHydrated({
            isAudienceHydrated,
            audienceType: values.audienceType,
            filters,
            persistedCriterias: group?.audience?.cycleAudienceCriterias ?? [],
        });

        try {
            if (group && cycleEvaluationGroupId) {
                await updateCycleEvaluationGroup({
                    id: cycleEvaluationGroupId,
                    payload: buildCycleEvaluationGroupUpdatePayload({
                        group,
                        name: values.name.trim(),
                        period,
                        // A draft's resume point moves on; a launched cycle has none left to move.
                        currentStep: isLaunched
                            ? undefined
                            : CycleEvaluationGroupSteps.EVALUATION_ELEMENTS,
                        audience,
                    }),
                });

                return true;
            }

            const created = await createCycleEvaluationGroup({
                name: values.name.trim(),
                ...period,
                currentStep: CycleEvaluationGroupSteps.EVALUATION_ELEMENTS,
                audience,
            });

            navigate({
                path: `${RouteName.CycleEvaluationGroupForm}/${getCycleEvaluationGroupStepPath(CycleEvaluationGroupSteps.EVALUATION_ELEMENTS)}/${created.id}`,
            });

            return false;
        } catch (error) {
            if (isDuplicateCycleNameError(error)) {
                showWarningToast({ message: t(DUPLICATE_NAME_MESSAGE) });

                return false;
            }

            handleError({ error });

            return false;
        }
    }, [
        isReadOnly,
        trigger,
        isNewCycle,
        group,
        cycleEvaluationGroupId,
        getValues,
        periods,
        offeredPeriodValues,
        filters,
        isAudienceHydrated,
        navigate,
        createCycleEvaluationGroup,
        updateCycleEvaluationGroup,
        t,
        handleError,
    ]);
};
